import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import api from '../lib/api';
import { setToken, setUser } from '../lib/auth';

export default function AuthPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('request'); // 'request' or 'verify'
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    phone: '',
    full_name: '',
    code: '',
  });
  const [otpCode, setOtpCode] = useState(''); // For display

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/api/auth/request-otp', {
        email: formData.email,
        phone: formData.phone,
      });

      setOtpCode(response.data.otp); // DEV only
      toast.success('OTP sent successfully!');
      toast.info(`Your OTP code is: ${response.data.otp}`, {
        duration: 10000,
      });
      setStep('verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/api/auth/verify-otp', {
        email: formData.email,
        phone: formData.phone,
        code: formData.code,
        full_name: formData.full_name || undefined,
      });

      setToken(response.data.token);
      setUser(response.data.user);
      toast.success('Successfully signed in!');
      navigate('/roles');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-charcoal-50 px-4 py-12"
      data-testid="auth-container"
    >
      <div className="max-w-md w-full bg-white  shadow-soft border border-charcoal-200 p-8">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <div className="w-14 h-14 bg-gradient-to-br from-[#FF3366] to-[#FF3366] rounded-xl flex items-center justify-center shadow-soft">
              <span className="text-white font-bold text-2xl">G</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-charcoal-900 mb-2">
            Welcome to girgit
          </h1>
          <p className="text-charcoal-600">
            {step === 'request'
              ? 'Sign in or create your account'
              : 'Verify your OTP'}
          </p>
        </div>

        {/* Request OTP Form */}
        {step === 'request' && (
          <form onSubmit={handleRequestOTP} className="space-y-6" aria-label="Request OTP form">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-charcoal-700 mb-2"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                required
                aria-required="true"
                aria-describedby="email-description"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full px-4 py-3 border border-charcoal-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 bg-white text-charcoal-900 placeholder-charcoal-400"
                placeholder="you@example.com"
                data-testid="email-input"
              />
            </div>

            <div>
              <label
                htmlFor="phone"
                className="block text-sm font-medium text-charcoal-700 mb-2"
              >
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                required
                aria-required="true"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className="w-full px-4 py-3 border border-charcoal-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 bg-white text-charcoal-900 placeholder-charcoal-400"
                placeholder="+1234567890"
                data-testid="phone-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              aria-busy={loading}
              aria-label={loading ? "Sending OTP..." : "Send OTP"}
              className="w-full px-6 py-3 bg-pink-600 text-white rounded-xl hover:bg-pink-700 font-medium shadow-soft hover:shadow disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              data-testid="request-otp-button"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2 w-5 h-5" />
                  Sending...
                </>
              ) : (
                'Send OTP'
              )}
            </button>
          </form>
        )}

        {/* Verify OTP Form */}
        {step === 'verify' && (
          <form onSubmit={handleVerifyOTP} className="space-y-6">
            {/* OTP will be sent to your mobile */}
            {otpCode && (
              <div className="p-4 bg-pink-50 border border-pink-200 rounded-xl">
                <p className="text-sm text-pink-800 font-medium mb-1">
                  Your OTP Code:
                </p>
                <p className="text-2xl font-bold text-pink-900 font-mono text-center">
                  {otpCode}
                </p>
                <p className="text-xs text-pink-700 mt-2 text-center">
                  Enter this code below to verify
                </p>
              </div>
            )}

            <div>
              <label
                htmlFor="full_name"
                className="block text-sm font-medium text-charcoal-700 mb-2"
              >
                Full Name
                <span className="text-neutral-500 font-normal ml-1">
                  (Required for new users)
                </span>
              </label>
              <input
                type="text"
                id="full_name"
                value={formData.full_name}
                onChange={(e) =>
                  setFormData({ ...formData, full_name: e.target.value })
                }
                className="w-full px-4 py-3 border border-charcoal-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 bg-white text-charcoal-900 placeholder-charcoal-400"
                placeholder="John Doe"
                data-testid="fullname-input"
              />
            </div>

            <div>
              <label
                htmlFor="code"
                className="block text-sm font-medium text-charcoal-700 mb-2"
              >
                OTP Code
              </label>
              <input
                type="text"
                id="code"
                required
                maxLength={6}
                value={formData.code}
                onChange={(e) =>
                  setFormData({ ...formData, code: e.target.value })
                }
                className="w-full px-4 py-3 border border-charcoal-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brutal-pink/30 focus:border-brutal-pink/50 bg-white text-charcoal-900 placeholder-charcoal-400 text-center text-2xl font-semibold tracking-widest"
                placeholder="000000"
                data-testid="otp-input"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => {
                  setStep('request');
                  setFormData({ ...formData, code: '', full_name: '' });
                  setOtpCode('');
                }}
                className="flex-1 px-6 py-3 bg-white text-charcoal-700 border-2 border-charcoal-200 rounded-xl hover:bg-charcoal-50 hover:border-charcoal-300 font-medium"
                data-testid="back-button"
              >
                Back
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-3 bg-teal-500 text-white rounded-xl hover:bg-teal-600 transition-all duration-200 font-medium shadow-soft hover:shadow-soft disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                data-testid="verify-otp-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin mr-2 w-5 h-5" />
                    Verifying...
                  </>
                ) : (
                  'Verify & Sign In'
                )}
              </button>
            </div>
          </form>
        )}

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <Link
            to="/"
            className="text-sm text-neutral-600 hover:text-pink-600 transition-colors"
          >
            ← Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}